import { FC, createContext, useContext, useEffect } from "react"
import { FetchStatus, IEntity } from "../../../../base/types"
import { useNavigate, useParams } from "react-router-dom"

import { Form } from "formik"
import { IFormPros } from "./IFormProps"
import { PageLinkHelper } from "../../../../base/helpers"
import { useDataContext } from "../../../page/context"

interface IFormDataContextState {
    values: IEntity
    initValues: IEntity
    isSubmitting: boolean
    saveFormData: (postSaveData?: () => Promise<void>) => Promise<void>
    setStatus: (status: string) => void
}

const context = createContext<IFormDataContextState>({
    values: {},
    initValues: {},
    isSubmitting: false,
    saveFormData: () => Promise.reject(),
    setStatus: () => { }
})

export const useFormDataContext = () => {
    return useContext(context)
}

interface IFormDataContextProps extends IFormPros {
    className?: string
    values: IEntity
    initValues: IEntity
    isSubmitting: boolean

    updateInitValues: (data: IEntity) => void,
    setSubmitting: (isSubmitting: boolean) => void
    setStatus: (status: string) => void

    children?: React.ReactNode
}

export const FormDataContext: FC<IFormDataContextProps> = ({
    systemId, autoSave, pageLink, title, children, className, initValues, values,
    isSubmitting, updateInitValues, setSubmitting, setStatus
}) => {
    const { status, saveData } = useDataContext()
    const { siteId } = useParams()
    const navigate = useNavigate()

    useEffect(() => {
        if (autoSave !== true || status !== FetchStatus.Success)
            return;

        const areNotEqualOrEmpty = () => {
            if (Object.keys(initValues).length === 0 || Object.keys(values).length === 0)
                return true;

            if (Object.keys(initValues).some(key => initValues[key] !== values[key]))
                return false;

            return true;
        }

        if (!areNotEqualOrEmpty()) {
            saveFormData().then(() => {
                if (pageLink && siteId) {
                    navigate(PageLinkHelper.getPageLinkUrl(siteId, pageLink))
                }
            });
        }
        // eslint-disable-next-line
    }, [status, values, initValues])

    const saveFormData = (postSaveData?: () => Promise<void>): Promise<void> => {
        setSubmitting(true)
        return saveData({ values, systemId }, postSaveData)
            .then((data) => {
                updateInitValues(data)
                return Promise.resolve()
            })
            .finally(() => setSubmitting(false))
    }

    return <>
        <context.Provider value={{ values, initValues, isSubmitting, saveFormData, setStatus }}>
            <Form className={className} title={title} noValidate>
                {children}
            </Form>
        </context.Provider>
    </>
}

